import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { Specialty } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/fetchSpecialties/types";
import { fetchSpecialties } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/fetchSpecialties/fetchSpecialtiesApi";
import {
  CreateSpecialtyRequest,
  CreateSpecialtyResponse,
} from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/createSpecialty/types";
import { createSpecialty } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/createSpecialty/createSpecialtyApi";
import {
  UpdateSpecialtyRequest,
  UpdateSpecialtyResponse,
} from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/updateSpecialty/types";
import { updateSpecialty } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/updateSpecialty/updateSpecialtyApi";
import { deleteSpecialty } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/deleteSpecialty/deleteSpecialtyApi";

interface SpecialtyState {
  list: Specialty[];
  loadingList: boolean;
  listError: string | null;

  loadingCreate: boolean;
  createError: string | null;

  loadingUpdate: boolean;
  updateError: string | null;

  loadingDelete: boolean;
  deleteError: string | null;
}

const initialState: SpecialtyState = {
  list: [],
  loadingList: false,
  listError: null,
  loadingCreate: false,
  createError: null,
  loadingUpdate: false,
  updateError: null,
  loadingDelete: false,
  deleteError: null,
};

const specialtySlice = createSlice({
  name: "employeeDirectorySpecialty",
  initialState,
  reducers: {
    setList(state, action: PayloadAction<Specialty[]>) {
      state.list = action.payload;
    },
    setLoadingList(state, action: PayloadAction<boolean>) {
      state.loadingList = action.payload;
    },
    setListError(state, action: PayloadAction<string | null>) {
      state.listError = action.payload;
    },
    setLoadingCreate(state, action: PayloadAction<boolean>) {
      state.loadingCreate = action.payload;
    },
    setCreateError(state, action: PayloadAction<string | null>) {
      state.createError = action.payload;
    },
    setLoadingUpdate(state, action: PayloadAction<boolean>) {
      state.loadingUpdate = action.payload;
    },
    setUpdateError(state, action: PayloadAction<string | null>) {
      state.updateError = action.payload;
    },
    setLoadingDelete(state, action: PayloadAction<boolean>) {
      state.loadingDelete = action.payload;
    },
    setDeleteError(state, action: PayloadAction<string | null>) {
      state.deleteError = action.payload;
    },
  },
});

export const {
  setList,
  setLoadingList,
  setListError,
  setLoadingCreate,
  setCreateError,
  setLoadingUpdate,
  setUpdateError,
  setLoadingDelete,
  setDeleteError,
} = specialtySlice.actions;

export default specialtySlice.reducer;

export const fetchSpecialtiesAction =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(setLoadingList(true));
    dispatch(setListError(null));
    try {
      const response = await fetchSpecialties();
      dispatch(setList(response.data));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch specialties.";
      dispatch(setListError(message));
    } finally {
      dispatch(setLoadingList(false));
    }
  };

export const createSpecialtyAction =
  (
    requestData: CreateSpecialtyRequest,
    onSuccess?: (created: CreateSpecialtyResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingCreate(true));
    dispatch(setCreateError(null));
    try {
      const response = await createSpecialty(requestData);
      onSuccess?.(response.data);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create specialty.";
      dispatch(setCreateError(message));
    } finally {
      dispatch(setLoadingCreate(false));
    }
  };

export const updateSpecialtyAction =
  (
    uuid: string,
    requestData: UpdateSpecialtyRequest,
    onSuccess?: (updated: UpdateSpecialtyResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingUpdate(true));
    dispatch(setUpdateError(null));
    try {
      const response = await updateSpecialty(uuid, requestData);
      onSuccess?.(response.data);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update specialty.";
      dispatch(setUpdateError(message));
    } finally {
      dispatch(setLoadingUpdate(false));
    }
  };

export const deleteSpecialtyAction =
  (uuid: string, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingDelete(true));
    dispatch(setDeleteError(null));
    try {
      await deleteSpecialty(uuid);
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete specialty.";
      dispatch(setDeleteError(message));
    } finally {
      dispatch(setLoadingDelete(false));
    }
  };
